"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Eye, EyeOff } from "lucide-react";
import { useTurnstile, isTurnstileError } from "@/hooks/use-turnstile";
import { useServiceFeeBps } from "@/components/tenant/TenantProvider";
import { checkoutApi } from "@/lib/api-client";
import { formatRupiah } from "@/lib/utils";
import { PAYMENT_METHODS, type PaymentMethodId } from "@/lib/payment-methods";
import type { SimpleProductInfo, SimpleProductKind } from "./useProductInfo";
interface UserInfo {
  name: string;
  email: string;
  phone: string | null;
}

/**
 * Form order ter-embed langsung di block (bukan redirect ke /order/[slug]) —
 * inti nilai jual halaman promosi ala Tribelio/ScaleV: isi data & checkout
 * tanpa pindah halaman.
 *
 * SENGAJA versi ringkas dari /order/[productSlug]/page-client.tsx (1496
 * baris) — v1 cakup jalur inti saja: harga tetap (TANPA varian/PWYW/kupon/
 * funnel bump), guest-atau-sudah-login, pilih metode bayar, submit. Fitur
 * yang di-skip itu tetap ada penuh di /order/[slug] biasa (mode redirect)
 * bagi tenant yang butuhnya. Ekstraksi FULL akan jadi pekerjaan terpisah
 * bila memang dibutuhkan.
 *
 * TIDAK bikin endpoint checkout baru — tetap POST /api/checkout/create-payment
 * yang sama (rate-limit + idempotency sudah teruji), cuma UI-nya yang beda.
 * Sukses pembayaran (non-gratis) tetap redirect ke /checkout/[orderId] —
 * gateway Pakasir eksternal tak bisa di-iframe.
 */
export default function EmbeddedOrderForm(t0) {
  const $ = _c(74);
  const {
    product,
    kind,
    slug
  } = t0;
  const router = useRouter();
  const feeRateBps = useServiceFeeBps();
  const errorRef = useRef(null);
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [user, setUser] = useState(null);
  const [mobile, setMobile] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "",
      email: "",
      phone: "",
      _hp_website: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [guestForm, setGuestForm] = useState(t1);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const turnstile = useTurnstile("turnstile-embedded-order");
  const [paymentMethod, setPaymentMethod] = useState("qris");
  const [ordering, setOrdering] = useState(false);
  const [error, setError] = useState("");
  const [idempotencyKey] = useState(_temp);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      fetch("/api/auth/me", {
        credentials: "include"
      }).then(_temp2).then(d => {
        if (!d.success) {
          setIsLoggedIn(false);
        } else {
          const u = d.data?.user || d.data;
          setIsLoggedIn(true);
          setUser({
            name: u.name || "",
            email: u.email || "",
            phone: u.phone || null
          });
        }
      }).catch(() => setIsLoggedIn(false));
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== error) {
    t4 = () => {
      if (error) {
        errorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
    };
    t5 = [error];
    $[3] = error;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  const isFree = kind === "course" && product.access_type === "free";
  let t6;
  if ($[6] !== feeRateBps || $[7] !== isFree || $[8] !== product.price) {
    t6 = isFree ? 0 : Math.round(product.price * feeRateBps / 10000);
    $[6] = feeRateBps;
    $[7] = isFree;
    $[8] = product.price;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const serviceFee = t6;
  const displayTotal = product.price + serviceFee;
  const needsMobile = isLoggedIn === true && !user?.phone;
  const turnstileRequired = isLoggedIn === false && turnstile.required;
  const turnstilePending = turnstileRequired && turnstile.pending;
  const disabled = ordering || product.sold_out || turnstilePending;
  let t7;
  if ($[10] !== router) {
    t7 = res => {
      const d_0 = res.data as {
        order_id?: string;
        free?: boolean;
        order_page_url?: string;
      } | undefined;
      if (res.success && d_0?.free) {
        window.location.href = d_0.order_page_url || (d_0.order_id ? `/checkout/success?order_id=${d_0.order_id}` : "/dashboard");
        return;
      }
      if (res.success && d_0?.order_id) {
        router.push(`/checkout/${d_0.order_id}`);
      } else {
        setError(res.error || "Gagal membuat order. Silakan coba lagi.");
        setOrdering(false);
      }
    };
    $[10] = router;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handleResult = t7;
  let t8;
  if ($[12] !== product.id || $[13] !== router || $[14] !== slug) {
    t8 = async () => {
      const res_0 = await fetch("/api/user/enroll/free", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          course_id: product.id
        })
      }).then(_temp3);
      if (res_0.success) {
        router.push(`/learn/${slug}/start`);
      } else {
        setError(res_0.error || "Gagal mendaftar. Silakan coba lagi.");
        setOrdering(false);
      }
    };
    $[12] = product.id;
    $[13] = router;
    $[14] = slug;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const enrollFree = t8;
  let t9;
  if ($[16] !== enrollFree || $[17] !== handleResult || $[18] !== idempotencyKey || $[19] !== isFree || $[20] !== kind || $[21] !== mobile || $[22] !== needsMobile || $[23] !== paymentMethod || $[24] !== product.id) {
    t9 = async () => {
      if (needsMobile && !mobile.trim()) {
        setError("Nomor WhatsApp wajib diisi");
        return;
      }
      setOrdering(true);
      setError("");
      if (isFree) {
        await enrollFree();
        return;
      }
      const res_1 = await checkoutApi.createPayment({
        order_type: kind,
        product_id: product.id,
        payment_method: paymentMethod,
        ...(mobile.trim() ? {
          mobile: mobile.trim()
        } : {})
      }, idempotencyKey);
      handleResult(res_1);
    };
    $[16] = enrollFree;
    $[17] = handleResult;
    $[18] = idempotencyKey;
    $[19] = isFree;
    $[20] = kind;
    $[21] = mobile;
    $[22] = needsMobile;
    $[23] = paymentMethod;
    $[24] = product.id;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const submitLoggedIn = t9;
  let t10;
  if ($[26] !== enrollFree || $[27] !== guestForm || $[28] !== handleResult || $[29] !== idempotencyKey || $[30] !== isFree || $[31] !== kind || $[32] !== password || $[33] !== paymentMethod || $[34] !== product.id || $[35] !== turnstile) {
    t10 = async () => {
      const {
        name,
        email,
        phone
      } = guestForm;
      if (!name.trim() || !email.trim() || !phone.trim()) {
        setError("Nama, email, dan WhatsApp wajib diisi");
        return;
      }
      setOrdering(true);
      setError("");
      const regRes = await fetch("/api/auth/register-guest", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          ...(password ? {
            password
          } : {}),
          cfTurnstileResponse: turnstile.token
        })
      }).then(_temp4);
      if (!regRes.success) {
        if (isTurnstileError(regRes.errorType)) {
          turnstile.noteServerRejection(regRes.errorType);
        } else {
          turnstile.reset();
        }
        setError(regRes.error || "Gagal membuat akun. Coba lagi.");
        setOrdering(false);
        return;
      }
      if (isFree) {
        await enrollFree();
        return;
      }
      const res_2 = await checkoutApi.createPayment({
        order_type: kind,
        product_id: product.id,
        payment_method: paymentMethod,
        mobile: phone.trim()
      }, idempotencyKey);
      handleResult(res_2);
    };
    $[26] = enrollFree;
    $[27] = guestForm;
    $[28] = handleResult;
    $[29] = idempotencyKey;
    $[30] = isFree;
    $[31] = kind;
    $[32] = password;
    $[33] = paymentMethod;
    $[34] = product.id;
    $[35] = turnstile;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const submitGuest = t10;
  let t11;
  if ($[37] !== isLoggedIn || $[38] !== submitGuest || $[39] !== submitLoggedIn) {
    t11 = () => isLoggedIn === false ? submitGuest() : submitLoggedIn();
    $[37] = isLoggedIn;
    $[38] = submitGuest;
    $[39] = submitLoggedIn;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  const submit = t11;
  if (isLoggedIn === null) {
    let t12;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <div className="h-64 animate-pulse rounded-2xl bg-gray-100" />;
      $[41] = t12;
    } else {
      t12 = $[41];
    }
    return t12;
  }
  let t12;
  if ($[42] !== guestForm || $[43] !== isLoggedIn || $[44] !== password || $[45] !== showPassword || $[46] !== turnstile) {
    t12 = isLoggedIn === false && <div className="mb-4 space-y-3"><input type="text" name="_hp_website" value={guestForm._hp_website} onChange={e => setGuestForm(p => ({
        ...p,
        _hp_website: e.target.value
      }))} style={{
        display: "none"
      }} tabIndex={-1} autoComplete="off" aria-hidden="true" />{([{
        key: "name",
        label: "Nama Lengkap",
        type: "text",
        autoComplete: "name"
      }, {
        key: "email",
        label: "Email",
        type: "email",
        autoComplete: "email"
      }, {
        key: "phone",
        label: "WhatsApp",
        type: "tel",
        autoComplete: "tel"
      }] as const).map(f => <div key={f.key}><label className="mb-1.5 block text-xs font-semibold text-gray-600">{f.label} <span className="text-red-500">*</span></label><input type={f.type} autoComplete={f.autoComplete} value={guestForm[f.key]} onChange={e_0 => setGuestForm(p_0 => ({
          ...p_0,
          [f.key]: e_0.target.value
        }))} className="w-full rounded-lg border border-[rgba(12,12,10,0.12)] bg-white px-3.5 py-2.5 text-sm text-warm-black outline-none transition-colors focus:border-primary" /></div>)}<div><label className="mb-1.5 block text-xs font-semibold text-gray-600">Password <span className="text-gray-400">(opsional)</span></label><div className="relative"><input type={showPassword ? "text" : "password"} value={password} onChange={e_1 => setPassword(e_1.target.value)} placeholder="Kosongkan untuk checkout tanpa password" className="w-full rounded-lg border border-[rgba(12,12,10,0.12)] bg-white px-3.5 py-2.5 text-sm text-warm-black outline-none transition-colors focus:border-primary pr-10" /><button type="button" onClick={() => setShowPassword(_temp5)} aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"} className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-lg text-gray-500">{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button></div></div>{turnstile.widget}</div>;
    $[42] = guestForm;
    $[43] = isLoggedIn;
    $[44] = password;
    $[45] = showPassword;
    $[46] = turnstile;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== isFree || $[49] !== isLoggedIn || $[50] !== mobile || $[51] !== user) {
    t13 = isLoggedIn && user && <div className="mb-4 space-y-3"><div><label className="mb-1.5 block text-xs font-semibold text-gray-600">Nama</label><input type="text" value={user.name} readOnly={true} className="w-full rounded-lg border border-[rgba(12,12,10,0.08)] bg-[rgba(12,12,10,0.03)] px-3.5 py-2.5 text-sm text-warm-black" /></div><div><label className="mb-1.5 block text-xs font-semibold text-gray-600">Email</label><input type="email" value={user.email} readOnly={true} className="w-full rounded-lg border border-[rgba(12,12,10,0.08)] bg-[rgba(12,12,10,0.03)] px-3.5 py-2.5 text-sm text-warm-black" /></div>{user.phone ? <div><label className="mb-1.5 block text-xs font-semibold text-gray-600">WhatsApp</label><input type="tel" value={user.phone} readOnly={true} className="w-full rounded-lg border border-[rgba(12,12,10,0.08)] bg-[rgba(12,12,10,0.03)] px-3.5 py-2.5 text-sm text-warm-black" /></div> : !isFree ? <div><label className="mb-1.5 block text-xs font-semibold text-gray-600">WhatsApp <span className="text-red-500">*</span></label><input type="tel" value={mobile} onChange={e_2 => setMobile(e_2.target.value)} placeholder="08xxxxxxxxxx" className="w-full rounded-lg border border-[rgba(12,12,10,0.12)] bg-white px-3.5 py-2.5 text-sm text-warm-black outline-none transition-colors focus:border-primary" /></div> : null}</div>;
    $[48] = isFree;
    $[49] = isLoggedIn;
    $[50] = mobile;
    $[51] = user;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  let t14;
  if ($[53] !== isFree || $[54] !== paymentMethod) {
    t14 = !isFree && <div className="mb-4"><p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Metode Pembayaran</p><div className="space-y-2">{PAYMENT_METHODS.map(m => <label key={m.id} className={`flex items-center gap-3 rounded-lg border p-3 text-sm transition-colors ${paymentMethod === m.id ? "border-primary bg-[var(--brand-primary-soft)]" : "border-[rgba(12,12,10,0.12)] bg-white"}`}><input type="radio" name="embedded-payment-method" checked={paymentMethod === m.id} onChange={() => setPaymentMethod(m.id)} className="h-4 w-4 text-primary focus:ring-primary" /><span className="flex-1 font-medium text-warm-black">{m.label}</span></label>)}</div></div>;
    $[53] = isFree;
    $[54] = paymentMethod;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] !== error) {
    t15 = error && <div ref={errorRef} className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>;
    $[56] = error;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== displayTotal || $[59] !== isFree) {
    t16 = <div>{isFree ? <p className="text-lg font-bold text-green-700">Gratis</p> : <p className="text-lg font-bold tabular-nums text-warm-black">{formatRupiah(displayTotal)}</p>}</div>;
    $[58] = displayTotal;
    $[59] = isFree;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  const t17 = ordering ? "Memproses..." : product.sold_out ? "Stok Habis" : isFree ? "Daftar Gratis \u2192" : "Bayar Sekarang \u2192";
  let t18;
  if ($[61] !== disabled || $[62] !== submit || $[63] !== t17) {
    t18 = <button onClick={submit} disabled={disabled} className="inline-flex h-12 items-center justify-center rounded-xl bg-[var(--brand-primary)] px-6 text-sm font-semibold text-[var(--brand-on-primary,#fff)] shadow-sm transition-colors hover:bg-[var(--brand-primary-hover)] disabled:cursor-not-allowed disabled:opacity-60">{t17}</button>;
    $[61] = disabled;
    $[62] = submit;
    $[63] = t17;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== t16 || $[66] !== t18) {
    t19 = <div className="flex items-center justify-between gap-4 border-t border-black/[0.06] pt-4">{t16}{t18}</div>;
    $[65] = t16;
    $[66] = t18;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== t12 || $[69] !== t13 || $[70] !== t14 || $[71] !== t15 || $[72] !== t19) {
    t20 = <div className="rounded-2xl border border-black/[0.08] bg-white p-5 shadow-sm sm:p-6">{t12}{t13}{t14}{t15}{t19}</div>;
    $[68] = t12;
    $[69] = t13;
    $[70] = t14;
    $[71] = t15;
    $[72] = t19;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  return t20;
}
function _temp5(v) {
  return !v;
}
function _temp4(r_1) {
  return r_1.json();
}
function _temp3(r_0) {
  return r_0.json();
}
function _temp2(r) {
  return r.json();
}
function _temp() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
}